import React, { useMemo, useState } from 'react';
import Title from '../Title';
import {
    ComplementaryServiceCategoryState,
    ComplementaryServiceTypeState,
    ZinantisHotelServiceFilterOption,
    ZinantisServiceState,
} from '../../../../types/properties/propertyTypes';
import Card from '../../../../muiComponents/Card';
import ServiceItem from './ServiceItem';
import SearchBarTypeahead, {
    ISearchBarTypeaheadOption,
} from '../../../components/SearchBarTypeahead';
import { Button, ButtonGroup } from '@mui/material';
import useTranslate from '../../../../customHooks/useTranslate';
import { getRandomNumber } from '../../../../utils/usefulFunctions';
import { getAttributesIncompleteSummary } from '../../../../utils/property/propertyZinantisUtils';

interface ServiceListDetailProps {
    title: string;
    services: ComplementaryServiceTypeState[];
    selectedServices: ZinantisServiceState[];
    categories: ComplementaryServiceCategoryState[];
    idSelectedCategory: number;
    onSelectCategory: (idCategory: number) => void;
    onToggleService: (zitoneoService: ZinantisServiceState) => void;
    onAddZitoneoService: (zitoneoService: ZinantisServiceState) => void;
    activeButton: ZinantisHotelServiceFilterOption;
    onActiveButtonChange: (option: ZinantisHotelServiceFilterOption) => void;
    visibleIdServicesDetail: number;
    onToggleVisibleServicesDetail: (idServiceType?: number) => void;
    onChangeZitoneoServiceValue: (
        id: string,
        value: string | boolean | Date | number | null,
        idService: string,
        idAttributeDefinition: number
    ) => void;
    onRemoveZitoneoServiceValue: (
        id: string,
        idService: string,
        idAttributeDefinition: number
    ) => void;
}

const ServiceListDetail = (props: ServiceListDetailProps) => {
    const {
        title,
        services,
        selectedServices,
        categories,
        idSelectedCategory,
        onSelectCategory,
        onToggleService,
        onAddZitoneoService,
        activeButton,
        onActiveButtonChange,
        visibleIdServicesDetail,
        onToggleVisibleServicesDetail,
        onChangeZitoneoServiceValue,
        onRemoveZitoneoServiceValue,
    } = props;
    const translate = useTranslate();
    const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
        null
    );

    const categoryNameById = useMemo(
        () =>
            categories.reduce<Record<number, string>>(
                (accumulator, category) => {
                    accumulator[parseInt(category.id)] = category.name;
                    return accumulator;
                },
                {}
            ),
        [categories]
    );

    const searchOptions = useMemo<
        ISearchBarTypeaheadOption<ComplementaryServiceTypeState>[]
    >(
        () =>
            services.map(service => ({
                id: service.id,
                label: service.name,
                value: service.id,
                group: categoryNameById[service.idServiceCategory] || undefined,
                data: service,
            })),
        [services, categoryNameById]
    );

    const selectedOption = useMemo(
        () =>
            searchOptions.find(
                option => option.id.toString() === selectedServiceId
            ) || null,
        [searchOptions, selectedServiceId]
    );

    const visibleServices = useMemo(() => {
        const servicesInCategory = idSelectedCategory
            ? services.filter(
                  service => service.idServiceCategory === idSelectedCategory
              )
            : services;

        const getSelectedService = (service: ComplementaryServiceTypeState) =>
            selectedServices.find(
                selected => selected.idServiceType === parseInt(service.id)
            );

        if (activeButton === ZinantisHotelServiceFilterOption.SELECTED) {
            return servicesInCategory.filter(service =>
                selectedServices.some(
                    selected => selected.idServiceType === parseInt(service.id)
                )
            );
        }

        if (activeButton === ZinantisHotelServiceFilterOption.INCOMPLETE) {
            return servicesInCategory.filter(service => {
                const selectedService = getSelectedService(service);

                if (!selectedService) {
                    return false;
                }

                const { incompleteCount } = getAttributesIncompleteSummary(
                    selectedService.attributeDefinitions,
                    selectedService.attributeValues
                );

                return incompleteCount > 0;
            });
        }

        if (
            activeButton === ZinantisHotelServiceFilterOption.MISSING_REQUIRED
        ) {
            return servicesInCategory.filter(service => {
                const selectedService = getSelectedService(service);

                if (!selectedService) {
                    return false;
                }

                const { requiredIncompleteCount } =
                    getAttributesIncompleteSummary(
                        selectedService.attributeDefinitions,
                        selectedService.attributeValues
                    );

                return requiredIncompleteCount > 0;
            });
        }

        return servicesInCategory;
    }, [services, selectedServices, activeButton, idSelectedCategory]);

    return (
        <div className='flex-grow flex flex-center'>
            <div className='zitoneo-services-page__content'>
                <header className='flex-column gap-10'>
                    <div className='flex flex-between'>
                        <Title
                            as='h1'
                            text={title}
                            className='overflow-initial'
                        />
                        <ButtonGroup>
                            {Object.values(
                                ZinantisHotelServiceFilterOption
                            ).map(option => (
                                <Button
                                    key={option}
                                    variant={
                                        activeButton === option
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    sx={{
                                        transition: 'none',
                                        borderColor:
                                            activeButton === option
                                                ? 'var(--primary) !important'
                                                : 'var(--border) !important',
                                        '&:hover': {
                                            borderColor:
                                                activeButton === option
                                                    ? 'var(--primary) !important'
                                                    : 'var(--border) !important',
                                        },
                                        '&.MuiButton-contained': {
                                            borderColor:
                                                'var(--primary) !important',
                                        },
                                        '&.MuiButton-outlined': {
                                            borderColor:
                                                'var(--border) !important',
                                        },
                                    }}
                                    onClick={() => onActiveButtonChange(option)}
                                >
                                    {translate(option)}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <SearchBarTypeahead
                        options={searchOptions}
                        value={selectedOption}
                        placeholder='Buscar servicio'
                        resetInputValueOnSelect={true}
                        onInputChange={value => {
                            if (!value) {
                                setSelectedServiceId(null);
                            }
                        }}
                        onSelect={option => {
                            if (!option) {
                                setSelectedServiceId(null);
                                return;
                            }

                            setSelectedServiceId(option.id.toString());

                            const selectedService = services.find(
                                service => service.id === option.id.toString()
                            );

                            if (selectedService) {
                                onSelectCategory(
                                    selectedService.idServiceCategory
                                );

                                const zitoneoService: ZinantisServiceState = {
                                    id: `${-getRandomNumber()}`,
                                    free: selectedService.free,
                                    idServiceType: parseInt(selectedService.id),
                                    serviceType: selectedService.name,
                                    idServiceCategory:
                                        selectedService.idServiceCategory,
                                    serviceCategory:
                                        categoryNameById[
                                            selectedService.idServiceCategory
                                        ] || '',
                                };
                                onAddZitoneoService(zitoneoService);
                                onActiveButtonChange(
                                    ZinantisHotelServiceFilterOption.ALL
                                );
                            }
                        }}
                    />
                </header>
                <div className='flex-grow overflow-auto padding-right-10'>
                    <Card>
                        <ul>
                            {visibleServices.map(service => {
                                const selectedService = selectedServices.find(
                                    selected =>
                                        selected.idServiceType ===
                                        parseInt(service.id)
                                );
                                const {
                                    incompleteCount,
                                    requiredIncompleteCount,
                                } = getAttributesIncompleteSummary(
                                    selectedService?.attributeDefinitions,
                                    selectedService?.attributeValues
                                );

                                return (
                                    <li
                                        key={service.id}
                                        className='zitoneo-service__item'
                                    >
                                        <ServiceItem
                                            service={service}
                                            attributeDefinitions={
                                                selectedService?.attributeDefinitions
                                            }
                                            attributeValues={
                                                selectedService?.attributeValues
                                            }
                                            selected={!!selectedService}
                                            onToggle={() => {
                                                const zitoneoService: ZinantisServiceState =
                                                    selectedService || {
                                                        id: `${-getRandomNumber()}`,
                                                        free: service.free,
                                                        idServiceType: parseInt(
                                                            service.id
                                                        ),
                                                        serviceType:
                                                            service.name,
                                                        idServiceCategory:
                                                            service.idServiceCategory,
                                                        serviceCategory:
                                                            categoryNameById[
                                                                service
                                                                    .idServiceCategory
                                                            ] || '',
                                                    };
                                                onToggleService(zitoneoService);
                                            }}
                                            isVisibleDetail={
                                                visibleIdServicesDetail ===
                                                parseInt(service.id)
                                            }
                                            onToggleVisibleDetail={
                                                onToggleVisibleServicesDetail
                                            }
                                            onChangeZitoneoServiceValue={
                                                onChangeZitoneoServiceValue
                                            }
                                            onRemoveZitoneoServiceValue={
                                                onRemoveZitoneoServiceValue
                                            }
                                            incompleteCount={incompleteCount}
                                            requiredIncompleteCount={
                                                requiredIncompleteCount
                                            }
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ServiceListDetail;
